import { useEffect, useState, type JSX } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@client/stores/auth';
import { logout } from '@client/api/auth';
import { useInstallPrompt } from '@client/pwa/useInstallPrompt';
import { applyDynamicManifest, resetDynamicManifest } from '@client/pwa/dynamic-manifest';
import { BrandMark } from '@ui/components/BrandMark';
import { Button } from '@ui/components/Button';
import { Modal } from '@ui/components/Modal';
import { GraceBanner } from '@ui/features/billing/GraceBanner';

type NavItem = { to: string; label: string; icon: JSX.Element; end?: boolean };

const NEW_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const LIST_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);
const SETTINGS_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);
const DOWNLOAD_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);
const SHIELD_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
  </svg>
);

export function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.me);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const isOwner = me?.user.role === 'platform_owner';

  // Tenants get the calculator + their own settings. platform_owner does not author
  // estimates (Capability.SaveEstimate gates them out anyway) and has no business —
  // they see only the Admin console.
  const navItems: NavItem[] = isOwner
    ? [{ to: '/admin', label: 'Admin', icon: SHIELD_ICON }]
    : [
        { to: '/', label: 'New', icon: NEW_ICON, end: true },
        { to: '/estimates', label: 'History', icon: LIST_ICON },
        { to: '/settings', label: 'Settings', icon: SETTINGS_ICON },
      ];

  const businessName = me?.business?.name ?? null;
  // Patch the PWA manifest + iOS apple-touch title with the business name so
  // Add-to-Home-Screen creates a shortcut labelled "Lakshmi Jewellers" rather
  // than the generic build-time "J Calculator". Runs whenever the business
  // name changes (login, business switch via platform_owner debugging, name
  // edit). No-op when name is empty.
  useEffect(() => {
    if (!businessName) return;
    void applyDynamicManifest(businessName);
  }, [businessName]);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      // Restore build-time manifest before navigating away so an install
      // triggered between logout and the next login captures the generic
      // product name, not the previous tenant's shop name.
      resetDynamicManifest();
      setUnauthenticated();
      queryClient.removeQueries({ queryKey: ['me'] });
      navigate('/login', { replace: true });
    },
  });

  return (
    <div className="flex min-h-dvh flex-col">
      <GraceBanner />
      <DesktopHeader
        navItems={navItems}
        userEmail={me?.user.email ?? ''}
        userFullName={me?.user.fullName ?? null}
        businessName={me?.business?.name ?? null}
        showProfileLink={!isOwner}
        onLogout={() => logoutMutation.mutate()}
        loggingOut={logoutMutation.isPending}
      />
      <main id="main" tabIndex={-1} className="flex-1 px-5 pb-10 pt-6 outline-none sm:px-8">
        <div className="mx-auto w-full max-w-5xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function DesktopHeader({
  navItems,
  userEmail,
  userFullName,
  businessName,
  showProfileLink,
  onLogout,
  loggingOut,
}: {
  navItems: NavItem[];
  userEmail: string;
  userFullName: string | null;
  businessName: string | null;
  showProfileLink: boolean;
  onLogout: () => void;
  loggingOut: boolean;
}): JSX.Element {
  // Trigger label prefers full name; falls back to email; finally generic 'Account'.
  const triggerLabel = userFullName || userEmail || 'Account';
  // Avatar initial: first letter of full name, else email, else '?'.
  const avatarSeed = userFullName || userEmail || '?';
  const [menuOpen, setMenuOpen] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const { canInstall, isIOS, isStandalone, promptInstall } = useInstallPrompt();
  // Show entry on mobile-class UAs only when the app isn't already installed and
  // the platform either supports the native prompt (Android/Chrome) or needs the
  // manual iOS Safari steps. Desktop dropdown stays clean.
  const showInstallEntry = !isStandalone && (canInstall || isIOS);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (): void => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  async function handleInstallClick(): Promise<void> {
    setMenuOpen(false);
    if (canInstall) {
      await promptInstall();
      return;
    }
    if (isIOS) setIosHelpOpen(true);
  }

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/80 backdrop-blur dark:border-slate-800/80 dark:bg-slate-950/80">
        <div className="mx-auto flex h-16 w-full max-w-5xl items-center gap-3 px-4 sm:px-8">
          <NavLink to="/" className="flex min-w-0 items-center gap-2.5">
            <BrandMark size="sm" />
            {/* Business name in header on every breakpoint. Falls back to the product
              name when the user has no business (free trial / platform owner). truncate
              keeps long shop names from pushing the nav off-screen on narrow phones. */}
            <span className="min-w-0 truncate text-base font-semibold tracking-tight">
              {businessName ?? 'J Calculator'}
            </span>
          </NavLink>
          <nav aria-label="Primary" className="ml-6 hidden items-center gap-1 sm:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                {...(item.end ? { end: true } : {})}
                className={({ isActive }) =>
                  `flex h-9 items-center gap-2 rounded-full px-3.5 text-sm font-medium transition ${
                    isActive
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="inline-flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-sm transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-200 text-xs font-semibold text-amber-900">
                {avatarSeed[0]!.toUpperCase()}
              </span>
              <span className="hidden max-w-[140px] truncate sm:inline">{triggerLabel}</span>
            </button>
            {menuOpen ? (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="border-b border-slate-100 px-4 py-3 text-sm dark:border-slate-800">
                  {userFullName ? <p className="font-medium">{userFullName}</p> : null}
                  <p className="truncate text-xs text-slate-500">{userEmail}</p>
                  <p className="mt-1 text-xs text-slate-400">{businessName ?? 'No business'}</p>
                </div>
                {/* Nav items live in dropdown on mobile (sm:hidden) — desktop has the
                  inline header nav above. Keeps a single source of truth for nav
                  list, no separate bottom-tab bar that fights with the keypad. */}
                <nav
                  aria-label="Primary mobile"
                  className="border-b border-slate-100 p-2 sm:hidden dark:border-slate-800"
                >
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      {...(item.end ? { end: true } : {})}
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition ${
                          isActive
                            ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100'
                            : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`
                      }
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  ))}
                  {showInstallEntry ? (
                    <button
                      type="button"
                      onClick={() => {
                        void handleInstallClick();
                      }}
                      className="flex h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      {DOWNLOAD_ICON}
                      Add to Home Screen
                    </button>
                  ) : null}
                </nav>
                {showProfileLink ? (
                  <div className="border-b border-slate-100 p-2 dark:border-slate-800">
                    <NavLink
                      to="/me/profile"
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition ${
                          isActive
                            ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100'
                            : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`
                      }
                    >
                      My profile
                    </NavLink>
                  </div>
                ) : null}
                <div className="p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    pending={loggingOut}
                    pendingLabel="Signing out…"
                    onClick={onLogout}
                  >
                    Sign out
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </header>
      <Modal
        open={iosHelpOpen}
        onClose={() => setIosHelpOpen(false)}
        title="Add to Home Screen"
        description="iOS Safari does not expose a one-tap install. Follow these steps to add the app to your Home Screen."
      >
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          <li>
            Tap the <span className="font-medium">Share</span> icon in Safari&apos;s toolbar.
          </li>
          <li>
            Scroll and tap <span className="font-medium">Add to Home Screen</span>.
          </li>
          <li>
            Confirm with <span className="font-medium">Add</span>.
          </li>
        </ol>
        <p className="mt-3 text-xs text-slate-500">
          If you don&apos;t see the option, make sure you&apos;re using Safari (not Chrome or in-app
          browsers).
        </p>
      </Modal>
    </>
  );
}
